import {
  AbsenceKindEnum,
  PortalVacationBucketFragment,
  usePortalAbsenceRequestMutation,
  usePortalAbsencesQuery,
} from "@dashboard/graphql";
import { Box, Button, Input, Option, Select, Text } from "@saleor/macaw-ui-next";
import { useMemo, useState } from "react";
import { useIntl } from "react-intl";

import { AbsenceList, useAbsenceKindLabel } from "../AbsenceList";
import { ErrorText, Page, Section } from "../components";
import { DispensationPeriods, PARTIAL_KINDS, Period } from "../DispensationPeriods";
import { portalLabels as l } from "../labels";
import { portalMessages as m } from "../messages";
import { RosterGrid } from "../RosterGrid";
import { addDays, formatDay, today } from "../utils";
import { VacationPreview } from "../VacationPreview";

/** Vacation days per year, counted up to today. ``detailed`` adds the
 * usable window, for gerência; colaboradores get a clean view. */
export const VacationBalance = ({
  buckets,
  detailed = false,
}: {
  buckets: PortalVacationBucketFragment[];
  detailed?: boolean;
}) => {
  const intl = useIntl();

  return (
    <Box display="grid" gap={3} __gridTemplateColumns="repeat(auto-fit, minmax(200px, 1fr))">
      {buckets
        .filter(bucket => bucket.usableUntil >= today())
        .map(bucket => (
          <Box
            key={bucket.year}
            padding={3}
            borderRadius={4}
            borderWidth={1}
            borderStyle="solid"
            borderColor="default1"
            display="flex"
            flexDirection="column"
            gap={1}
          >
            <Text size={2} color="default2">
              {bucket.carried
                ? intl.formatMessage(m.carriedOver, {
                    year: bucket.year,
                    until: formatDay(bucket.usableUntil),
                  })
                : intl.formatMessage(m.vacationYear, { year: bucket.year })}
            </Text>
            <Text size={7} fontWeight="bold">
              {intl.formatMessage(m.daysLeft, { left: bucket.remaining, total: bucket.days })}
            </Text>
            <Text size={2} color="default2">
              {intl.formatMessage(m.vacationCounts, {
                taken: bucket.taken,
                booked: bucket.booked,
                pending: bucket.pending,
              })}
            </Text>
            {bucket.bonus > 0 && (
              <Text size={2} color="success1">
                {intl.formatMessage(bucket.bonusPending ? m.bonusIfApproved : m.bonusEarned, {
                  days: bucket.bonus,
                })}
              </Text>
            )}
            {detailed && !bucket.carried && (
              <Text size={1} color="default2">
                {intl.formatMessage(m.vacationWindow, {
                  from: formatDay(bucket.usableFrom),
                  until: formatDay(bucket.usableUntil),
                })}
              </Text>
            )}
          </Box>
        ))}
    </Box>
  );
};

const monthStart = (day: string) => `${day.slice(0, 7)}-01`;

export const Absences = () => {
  const intl = useIntl();
  const absenceKindLabel = useAbsenceKindLabel();
  const [month, setMonth] = useState(() => monthStart(today()));
  const monthEnd = addDays(monthStart(addDays(month, 32)), -1);
  const days = useMemo(() => {
    const out: string[] = [];

    for (let d = month; d <= monthEnd; d = addDays(d, 1)) {
      out.push(d);
    }

    return out;
  }, [month, monthEnd]);
  const { data, refetch } = usePortalAbsencesQuery({
    variables: { start: month, end: monthEnd },
    fetchPolicy: "cache-and-network",
  });
  const [form, setForm] = useState({
    kind: AbsenceKindEnum.VACATION as string,
    startDate: "",
    endDate: "",
    reason: "",
  });
  const [periods, setPeriods] = useState<Period[]>([{ start: "16:00", end: "18:00" }]);
  const partial = PARTIAL_KINDS.includes(form.kind);
  const [request, requestOpts] = usePortalAbsenceRequestMutation({
    onCompleted: result => {
      if (!result.absenceRequest?.errors.length) {
        setForm({ ...form, startDate: "", endDate: "", reason: "" });
        refetch();
      }
    },
  });
  const kindOptions: Option[] = Object.values(AbsenceKindEnum)
    .filter(kind => kind !== AbsenceKindEnum.UNJUSTIFIED)
    .map(kind => ({ value: kind, label: absenceKindLabel(kind) }));
  const calendar = data?.absenceCalendar ?? [];
  const people = [...new Map(calendar.map(row => [row.userId, row.employeeName]))].map(
    ([userId, name]) => ({ userId, name }),
  );
  const cells = calendar.flatMap(row =>
    days
      .filter(day => day >= row.startDate && day <= row.endDate)
      .map(day => ({
        userId: row.userId,
        date: day,
        label:
          (row.kind ? absenceKindLabel(row.kind) : intl.formatMessage(l.absent)) +
          (row.pending ? " ?" : ""),
        color: row.pending ? "#fef3c7" : "#dbeafe",
      })),
  );

  return (
    <Page>
      <Section title={intl.formatMessage(m.vacationBalance)}>
        <VacationBalance buckets={data?.myVacationBalance ?? []} />
      </Section>
      <Section title={intl.formatMessage(m.requestAbsence)}>
        <Box display="grid" gap={2} __gridTemplateColumns="repeat(auto-fit, minmax(170px, 1fr))">
          <Select
            size="small"
            label={intl.formatMessage(m.kind)}
            options={kindOptions}
            value={kindOptions.find(o => o.value === form.kind) ?? null}
            onChange={option => setForm({ ...form, kind: option.value })}
          />
          <Input
            size="small"
            type="date"
            label={intl.formatMessage(m.start)}
            value={form.startDate}
            onChange={event =>
              setForm({
                ...form,
                startDate: event.target.value,
                endDate: form.endDate || event.target.value,
              })
            }
          />
          <Input
            size="small"
            type="date"
            label={intl.formatMessage(m.end)}
            value={form.endDate}
            onChange={event => setForm({ ...form, endDate: event.target.value })}
          />
          <Input
            size="small"
            label={intl.formatMessage(m.reason)}
            value={form.reason}
            maxLength={2000}
            onChange={event => setForm({ ...form, reason: event.target.value })}
          />
        </Box>
        {partial ? (
          <DispensationPeriods periods={periods} onChange={setPeriods} />
        ) : (
          <VacationPreview
            start={form.startDate}
            end={form.endDate}
            vacation={form.kind === AbsenceKindEnum.VACATION}
          />
        )}
        <Box>
          <Button
            size="small"
            variant="primary"
            disabled={!form.startDate || !form.endDate || requestOpts.loading}
            onClick={() =>
              request({
                variables: {
                  ...form,
                  kind: form.kind as AbsenceKindEnum,
                  dailyPeriods: partial ? periods : undefined,
                },
              })
            }
          >
            {intl.formatMessage(m.send)}
          </Button>
        </Box>
        <ErrorText errors={requestOpts.data?.absenceRequest?.errors} />
        <Text size={1} color="default2">
          {intl.formatMessage(m.proofHint)}
        </Text>
      </Section>
      <Section title={intl.formatMessage(m.myAbsences)}>
        <AbsenceList absences={data?.myAbsences ?? []} onChanged={refetch} />
      </Section>
      <Section
        title={intl.formatMessage(m.teamCalendar)}
        actions={
          <Box display="flex" gap={2} alignItems="center">
            <Button
              variant="secondary"
              size="small"
              onClick={() => setMonth(monthStart(addDays(month, -1)))}
            >
              ‹
            </Button>
            <Text size={3}>{month.slice(0, 7)}</Text>
            <Button
              variant="secondary"
              size="small"
              onClick={() => setMonth(monthStart(addDays(month, 32)))}
            >
              ›
            </Button>
          </Box>
        }
      >
        <RosterGrid
          days={days}
          people={people}
          cells={cells}
          renderCell={(_, __, cell) => <Text size={1}>{cell?.label ?? ""}</Text>}
        />
      </Section>
    </Page>
  );
};
